import { useQuery } from '@tanstack/react-query';
import { API } from 'aws-amplify';
import React from 'react';
import { AuthContext } from '../contexts/auth-context';
import { GettingStartedHomeData } from '../types';
import { captureError } from '../utils/capture-error';
import { isAgt } from '../utils/is-agent';

/**
 * Getting Started step status for the agent being viewed (impersonated,
 * staff's agent or the user). Shared by Home and the App Bar's Getting
 * started panel, so both read one cache entry. Disabled for admins who aren't
 * impersonating (no agent number).
 *
 * @param enabled - Pass false to skip the request (default true)
 * @param refetchOnMount - Pass false for a second reader that mounts after
 *   the first fetch, so it doesn't fetch (or retry a failed fetch) again
 *   (default true)
 */
export function useGettingStarted(params?: {
  enabled?: boolean;
  refetchOnMount?: boolean;
}) {
  // Context
  const {
    state: { user, impersonatedAgent, session },
  } = React.useContext(AuthContext);

  const AgtNo =
    impersonatedAgent?.AgtNo ||
    session?.getAccessToken().decodePayload()['myStaffForAgt'] ||
    user?.getUsername();

  // Query - Getting started
  const path = '/agent/getting-started';
  const query = useQuery({
    enabled: params?.enabled !== false && isAgt(AgtNo),
    queryKey: [path, { AgtNo }],
    refetchOnMount: params?.refetchOnMount !== false,
    retryOnMount: params?.refetchOnMount !== false,
    queryFn: async () => {
      if (isAgt(AgtNo)) {
        const response: {
          data: GettingStartedHomeData;
        } = await API.post('ContractingAPI', path, {
          body: { AgtNo },
        });

        if (response.data) {
          return response.data;
        }
      }

      return null;
    },
    onError: (error) => captureError({ data: { error } }),
  });

  return { AgtNo, query };
}
